import { Link, useNavigate, useParams } from 'react-router-dom';
import { get, post } from '../api';
import { useMe } from '../auth';
import {
  ACTION_LABEL,
  ActionBadge,
  Alert,
  Card,
  Empty,
  fmtDate,
  humanize,
  PageHeader,
  ScoreBar,
  SeverityBadge,
  Spinner,
  StatusBadge,
  useAction,
  useLoader,
} from '../components/ui';
import { ACTIONS, type Action, type MessageDetail, type Settings } from '../types';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="kv">
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

export function MessageDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useMe();
  const { data, error, loading, reload } = useLoader(() => get<MessageDetail>(`/api/messages/${encodeURIComponent(id)}`), [id]);
  const { data: settings } = useLoader(() => get<Settings>('/api/settings'), []);
  const act = useAction();

  if (loading && !data) return <Spinner />;
  if (!data) return <Alert>{error ?? 'Message not found'}</Alert>;

  const { message: m, context, client_context, webhook_deliveries, end_user } = data;
  const related = new Set(m.related_message_ids ?? []);
  const fragmented = related.size > 1;
  const flagThreshold = settings?.moderation.thresholds.flag;

  const review = (action: Action) =>
    act.run(`review-${action}`, async () => {
      await post(`/api/messages/${m.id}/review`, { action });
      await reload(true);
    });
  const reprocess = () =>
    act.run('reprocess', async () => {
      await post(`/api/messages/${m.id}/reprocess`);
      await reload(true);
      // The worker is usually fast; refresh once more to show the new decision.
      setTimeout(() => void reload(true), 1500);
    });
  const unblock = () =>
    act.run('unblock', async () => {
      await post(`/api/end-users/${encodeURIComponent(m.end_user_id)}/unblock`);
      await reload(true);
    });

  return (
    <>
      <div className="crumbs">
        <button className="btn btn-ghost btn-sm" onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/messages'))}>
          ← Back to messages
        </button>
      </div>
      <PageHeader
        title={`Message ${m.external_id}`}
        subtitle={
          <>
            from <Link to={`/messages?user_id=${encodeURIComponent(m.end_user_id)}&include_allowed=1`} className="mono">{m.end_user_id}</Link>
            {m.end_user_name && ` (${m.end_user_name})`}
            {m.conversation_id && (
              <>
                {' '}in{' '}
                <Link to={`/messages?conversation_id=${encodeURIComponent(m.conversation_id)}&include_allowed=1`} className="mono">
                  #{m.conversation_id}
                </Link>
              </>
            )}{' '}
            · {fmtDate(m.sent_at, true)}
          </>
        }
        actions={
          isAdmin && (
            <div className="btn-row">
              <button className="btn" disabled={!!act.busy} onClick={reprocess}>
                {act.busy === 'reprocess' ? 'Queuing…' : 'Reprocess'}
              </button>
            </div>
          )
        }
      />
      <Alert>{act.error}</Alert>

      <div className="detail-grid">
        <div className="detail-main">
          <Card title="Content">
            <blockquote className={`message-content ${fragmented ? 'is-fragment' : ''}`}>{m.content}</blockquote>
            {m.status === 'failed' && <Alert>Processing failed: {m.error}</Alert>}
            {Object.keys(m.metadata ?? {}).length > 0 && (
              <details className="details">
                <summary>Metadata</summary>
                <pre className="json">{JSON.stringify(m.metadata, null, 2)}</pre>
              </details>
            )}
          </Card>

          <Card
            title="Conversation context"
            subtitle={
              fragmented
                ? 'Highlighted messages were evaluated together as fragmented content.'
                : m.conversation_id
                  ? 'Messages in this conversation during the hour before this one.'
                  : "This user's earlier messages outside conversations."
            }
          >
            {context.length === 0 ? (
              <Empty>No stored context.</Empty>
            ) : (
              <ol className="context-list">
                {context.map((c) => {
                  const isSelf = c.id === m.id;
                  const isRelated = related.has(c.external_id) && fragmented;
                  return (
                    <li key={c.id} className={`${isSelf ? 'is-self' : ''} ${isRelated ? 'is-related' : ''}`}>
                      <div className="context-meta">
                        <span className="mono">{c.end_user_id}</span>
                        <span className="muted small">{fmtDate(c.sent_at, true)}</span>
                        {isRelated && <span className="badge badge-frag">fragment</span>}
                        {isSelf ? <span className="badge badge-muted">this message</span> : <ActionBadge action={c.action} />}
                      </div>
                      {isSelf ? (
                        <div className="context-text">{c.content}</div>
                      ) : (
                        <Link to={`/messages/${c.id}`} className="context-text">
                          {c.content}
                        </Link>
                      )}
                    </li>
                  );
                })}
              </ol>
            )}
            {fragmented && (
              <p className="small muted">
                Related message IDs: {[...related].map((r) => <code key={r} className="tag">{r}</code>)}
              </p>
            )}
          </Card>

          {client_context.length > 0 && (
            <Card title="Client-supplied context" subtitle="previous_messages sent with the request">
              <ol className="context-list">
                {client_context.map((c, i) => (
                  <li key={c.message_id ?? i} className={c.message_id && related.has(c.message_id) ? 'is-related' : ''}>
                    <div className="context-meta">
                      <span className="mono">{c.user_id}</span>
                      {c.message_id && <code className="tag">{c.message_id}</code>}
                      {c.timestamp && <span className="muted small">{fmtDate(c.timestamp, true)}</span>}
                    </div>
                    <div className="context-text">{c.content}</div>
                  </li>
                ))}
              </ol>
            </Card>
          )}

          <Card title="Webhook deliveries">
            {webhook_deliveries.length === 0 ? (
              <Empty>No webhook was sent for this message.</Empty>
            ) : (
              <div className="table-wrap">
                <table className="table table-compact">
                  <thead>
                    <tr>
                      <th>Event</th>
                      <th>Status</th>
                      <th className="num">Attempts</th>
                      <th>Response</th>
                      <th>Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {webhook_deliveries.map((d) => (
                      <tr key={d.id}>
                        <td className="mono">{d.event}</td>
                        <td>
                          <StatusBadge status={d.status} />
                        </td>
                        <td className="num">{d.attempts}</td>
                        <td className="small">{d.last_status_code ?? ''} {d.last_error && <span className="tone-text-bad">{d.last_error}</span>}</td>
                        <td className="nowrap muted">{fmtDate(d.created_at, true)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </div>

        <aside className="detail-side">
          <Card title="Decision">
            <div className="decision-head">
              {m.status === 'done' ? <ActionBadge action={m.action} /> : <StatusBadge status={m.status} />}
              {m.review_status && (
                <StatusBadge status={m.review_status} label={m.review_status === 'confirmed' ? 'confirmed by reviewer' : 'changed by reviewer'} />
              )}
            </div>
            <dl className="kvs">
              {m.model_action && m.model_action !== m.action && (
                <Field label="Model decided">
                  <ActionBadge action={m.model_action} />
                </Field>
              )}
              <Field label="Category">{m.category && m.category !== 'none' ? humanize(m.category) : '—'}</Field>
              <Field label="Severity">
                <SeverityBadge severity={m.severity} />
              </Field>
              <Field label="Risk">{m.risk == null ? '—' : <span className="mono">{m.risk.toFixed(2)}</span>}</Field>
              <Field label="Model">{m.jev_model ?? '—'}</Field>
              <Field label="Received">{fmtDate(m.received_at, true)}</Field>
              <Field label="Processed">{fmtDate(m.processed_at, true)}</Field>
              {m.reviewed_at && (
                <Field label="Reviewed">
                  {fmtDate(m.reviewed_at, true)}
                  {m.reviewer_email && <span className="muted"> by {m.reviewer_email}</span>}
                </Field>
              )}
            </dl>
            {m.flags.length > 0 && (
              <div className="tags">
                {m.flags.map((f) => (
                  <span key={f} className={`tag ${f === 'fragmented_content' ? 'tag-frag' : ''}`}>
                    {f}
                  </span>
                ))}
              </div>
            )}
            {isAdmin && m.status === 'done' && (
              <div className="review-actions">
                <p className="small muted">
                  Set the decision. Your server gets a <code>message.reviewed</code> webhook with it.
                </p>
                {ACTIONS.map((a) => (
                  <button
                    key={a}
                    className={`btn btn-block ${m.action === a ? 'btn-primary' : ''}`}
                    disabled={!!act.busy}
                    onClick={() => review(a)}
                    title={m.action === a ? 'Confirm this decision' : undefined}
                  >
                    {act.busy === `review-${a}` ? 'Saving…' : `${m.action === a ? '✓ ' : ''}${ACTION_LABEL[a]}`}
                  </button>
                ))}
                {end_user?.status === 'blocked' && m.action !== 'block_message_and_user' && (
                  <p className="small tone-text-bad">
                    {end_user.external_id} is still blocked, so their new messages keep being blocked. Unblock them under End user.
                  </p>
                )}
              </div>
            )}
            {isAdmin && m.status === 'failed' && (
              <p className="small muted">Reprocess this message to get a decision; you can review it after that.</p>
            )}
          </Card>

          <Card title="Scores">
            {m.scores ? (
              <div className="scores">
                <ScoreBar label="Toxic" value={m.scores.toxic} threshold={flagThreshold} />
                <ScoreBar label="Terms violation" value={m.scores.terms_violation} threshold={flagThreshold} />
                <ScoreBar label="Fragmented" value={m.scores.fragmented} threshold={flagThreshold} />
                {m.scores.category_confidence != null && (
                  <p className="small muted">Category confidence {Math.round(m.scores.category_confidence * 100)}%</p>
                )}
              </div>
            ) : (
              <Empty>Not scored yet.</Empty>
            )}
          </Card>

          <Card title="Reasons">
            {m.reasons?.length ? (
              <ul className="reasons">
                {m.reasons.map((r, i) => (
                  <li key={i}>{r}</li>
                ))}
              </ul>
            ) : (
              <Empty>No reasons recorded.</Empty>
            )}
          </Card>

          <Card title="End user">
            {end_user ? (
              <>
                <div className="decision-head">
                  <span className="mono">{end_user.external_id}</span>
                  <StatusBadge status={end_user.status} />
                </div>
                <dl className="kvs">
                  {end_user.display_name && <Field label="Name">{end_user.display_name}</Field>}
                  <Field label="Messages">{end_user.message_count}</Field>
                  <Field label="Violations">{end_user.violation_count}</Field>
                  <Field label="Last violation">{fmtDate(end_user.last_violation_at)}</Field>
                  {end_user.blocked_at && <Field label="Blocked">{fmtDate(end_user.blocked_at)}</Field>}
                </dl>
                {end_user.blocked_reason && <p className="small muted">{end_user.blocked_reason}</p>}
                {isAdmin && end_user.status === 'blocked' && (
                  <button className="btn btn-block" disabled={!!act.busy} onClick={unblock}>
                    {act.busy === 'unblock' ? 'Unblocking…' : 'Unblock user'}
                  </button>
                )}
              </>
            ) : (
              <Empty>Unknown user.</Empty>
            )}
          </Card>
        </aside>
      </div>
    </>
  );
}
